import { videos } from '@/mocks/videos';
import { categories } from '@/mocks/categories';
import type { Video, Category } from '@/types/content';

export const allVideos: Video[] = videos as unknown as Video[];
export const allCategories: Category[] = categories as unknown as Category[];

export const YEAR_OPTIONS = [
  { label: '全部年份', value: 'all' },
  { label: '2024', value: '2024' },
  { label: '2023', value: '2023' },
  { label: '2022', value: '2022' },
  { label: '2021及更早', value: 'older' },
];

export const DURATION_OPTIONS = [
  { label: '全部时长', value: 'all' },
  { label: '短片（15分钟以内）', value: 'short' },
  { label: '中篇（15-60分钟）', value: 'medium' },
  { label: '长篇（60分钟以上）', value: 'long' },
];

export const SORT_OPTIONS = [
  { label: '最新上线', value: 'latest' },
  { label: '最多播放', value: 'hot' },
  { label: '评分最高', value: 'rating' },
];

export function getCategory(key: string): Category | undefined {
  return allCategories.find((c) => c.key === key);
}

export function getVideoById(id: string): Video | undefined {
  return allVideos.find((v) => v.id === id);
}

export function getVideosByCategory(key: string): Video[] {
  return allVideos.filter((v) => v.category === key);
}

export function getFeatured(): Video[] {
  const list = allVideos.filter((v) => v.featured);
  return list.length > 0 ? list : allVideos.slice(0, 4);
}

export function getLatest(count: number): Video[] {
  return [...allVideos]
    .sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime())
    .slice(0, count);
}

export function getHot(count: number): Video[] {
  return [...allVideos].sort((a, b) => b.plays - a.plays).slice(0, count);
}

export function getRelated(video: Video, count: number): Video[] {
  const sameCategory = allVideos.filter((v) => v.category === video.category && v.id !== video.id);
  const others = allVideos.filter((v) => v.category !== video.category && v.id !== video.id);
  const shuffledSame = [...sameCategory].sort(() => Math.random() - 0.5);
  const shuffledOthers = [...others].sort(() => Math.random() - 0.5);
  return [...shuffledSame, ...shuffledOthers].slice(0, count);
}

export function searchVideos(keyword: string): Video[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  return allVideos.filter(
    (v) => v.title.toLowerCase().includes(q) || v.description.toLowerCase().includes(q)
  );
}

export interface FilterState {
  type: string;
  year: string;
  duration: string;
}

export function applyFilters(list: Video[], filters: FilterState): Video[] {
  return list.filter((v) => {
    if (filters.type && filters.type !== 'all' && v.category !== filters.type) return false;
    if (filters.year && filters.year !== 'all') {
      if (filters.year === 'older') {
        if (v.year > 2021) return false;
      } else if (String(v.year) !== filters.year) {
        return false;
      }
    }
    if (filters.duration && filters.duration !== 'all') {
      const mins = parseInt(v.duration, 10) || 0;
      if (filters.duration === 'short' && mins > 15) return false;
      if (filters.duration === 'medium' && (mins <= 15 || mins > 60)) return false;
      if (filters.duration === 'long' && mins <= 60) return false;
    }
    return true;
  });
}

export function sortVideos(list: Video[], sort: string): Video[] {
  const copy = [...list];
  if (sort === 'hot') return copy.sort((a, b) => b.plays - a.plays);
  if (sort === 'rating') return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort((a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime());
}

export function formatPlays(plays: number): string {
  if (plays >= 10000) return `${(plays / 10000).toFixed(1)}万`;
  return String(plays);
}

export function formatDurationShort(duration: string): string {
  return duration.replace('分钟', '');
}